import {
  BasicButton,
  Container,
  Grid,
  Layout,
  PrimaryButton,
  ProgressLoader,
  SecondaryTopNav,
  Stack,
  Text,
} from "@certn/cascadia-react";
import { t } from "i18next";
import { PropsWithChildren, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

import { VerificationTemplatesContext } from "./VerificationTemplatesContext";
import { CancelModal } from "../CancelModal";

interface Props extends PropsWithChildren {
  disableCompletion?: boolean;
  submitting?: boolean;
  onComplete?: () => void;
}

export function VerificationTemplatePageLayout(props: Props) {
  const { disableCompletion, submitting, onComplete, children } = props;

  const navigate = useNavigate();
  const { isLastStep, numberOfSteps, currentStep, goToPrevStep } = useContext(
    VerificationTemplatesContext
  );
  const [showCancelModal, setShowCancelModal] = useState(false);

  const handleCancel = () => {
    setShowCancelModal(false);
    navigate("/settings/employment-verification-templates");
  };

  const allowBackOrCancel = currentStep > 0;
  const isFirstStep = currentStep === 0;
  return (
    <Layout
      layout="narrow"
      header={
        <SecondaryTopNav
          testID="close-button"
          leftIcon={isFirstStep ? "close" : "arrow_back"}
          onleftIconOnPress={
            isFirstStep ? () => setShowCancelModal(true) : goToPrevStep
          }
        >
          {allowBackOrCancel && (
            <BasicButton
              onPress={() => {
                setShowCancelModal(true);
              }}
              isLoading={submitting}
              testID="emp-verification-template-cancel-button"
            >
              {t("Cancel")}
            </BasicButton>
          )}
          <PrimaryButton
            onPress={() => {
              onComplete?.();
            }}
            isDisabled={disableCompletion}
            isLoading={submitting}
          >
            {isLastStep ? t("Save template") : t("Next step")}
          </PrimaryButton>
        </SecondaryTopNav>
      }
    >
      <Container>
        <div>
          <Text style="input/section-title">
            {t(`step {{currentStep}} of {{numberOfSteps}}`, {
              currentStep: currentStep + 1,
              numberOfSteps,
            })}
          </Text>
          <Stack gap="space-3">
            <Text as="h1" style="global/title-medium">
              {t("Create employment verification template")}
            </Text>
            <ProgressLoader
              size="small"
              floating
              value={currentStep === 0 ? 0 : 50}
            />
          </Stack>
        </div>

        <Grid form>{children}</Grid>
        {showCancelModal && (
          <CancelModal
            onCancel={handleCancel}
            onClose={() => setShowCancelModal(false)}
          />
        )}
      </Container>
    </Layout>
  );
}
